import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from './ui/AppLogo';
import {
  Menu,
  X,
  LogOut,
  User,
  LayoutDashboard,
  ShoppingBag,
} from 'lucide-react';

export default function AppHeader({
  userName,
  userRole,
  onLogout,
  currentPath,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const activePath = currentPath ?? location.pathname;
  const isLoggedIn = !!userName;

  const navLinks = [
    {
      href: '/marketplace',
      label: 'Marketplace',
      icon: ShoppingBag,
    },
    ...(isLoggedIn
      ? [
          {
            href: '/dashboard',
            label: 'Dashboard',
            icon: LayoutDashboard,
          },
        ]
      : []),
  ];

  return (
    <header
      className="sticky top-0 z-50 border-b-2 border-foreground"
      style={{
        backgroundColor: 'var(--card)',
        boxShadow: '0 2px 0 var(--foreground)',
      }}
    >
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
        <div className="flex items-center justify-between h-16">

{/* Logo */}
<Link to="/" className="flex items-center gap-3 group">
  <img
    src="/assets/images/Logo1.png"
    alt="Clearance Connect Logo"
    className="h-12 w-auto object-contain"
  />

  <div className="flex flex-col leading-none">
    <span
      className="text-lg font-heading text-foreground"
      style={{
        fontFamily: 'var(--font-heading)',
        letterSpacing: '0.06em',
      }}
    >
      CLEARANCE
    </span>

    <span
      className="text-xs font-heading"
      style={{
        fontFamily: 'var(--font-heading)',
        letterSpacing: '0.12em',
        color: 'var(--primary)',
      }}
    >
      CONNECT
    </span>
  </div>
</Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = activePath === link.href;

              return (
                <Link
                  key={`nav-${link.href}`}
                  to={link.href}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-heading tracking-wide transition-all duration-150 ${
                    isActive ? 'tab-active' : 'tab-inactive'
                  }`}
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  <link.icon size={14} />
                  {link.label.toUpperCase()}
                </Link>
              );
            })}
          </nav>

          {/* Right Side */}
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <div className="flex items-center gap-2 px-3 py-1.5 border border-border">
                  <User
                    size={14}
                    className="text-muted-foreground"
                  />

                  <div className="flex flex-col leading-none">
                    <span className="text-xs font-heading tracking-wide text-foreground">
                      {userName?.toUpperCase()}
                    </span>

                    <span
                      className="text-xs font-mono-data"
                      style={{
                        color: 'var(--primary)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {userRole?.toUpperCase()}
                    </span>
                  </div>
                </div>

                <button
                  onClick={onLogout}
                  className="btn-danger flex items-center gap-1.5 px-3 py-2 text-xs"
                >
                  <LogOut size={13} />
                  LOGOUT
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="btn-secondary px-4 py-2 text-sm"
                >
                  LOG IN
                </Link>

                <Link
                  to="/login?tab=register"
                  className="btn-primary px-4 py-2 text-sm"
                >
                  REGISTER
                </Link>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden p-2 border border-border"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle mobile menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div
          className="md:hidden border-t-2 border-foreground"
          style={{ backgroundColor: 'var(--card)' }}
        >
          <div className="px-4 py-4 flex flex-col gap-3">

            {navLinks.map((link) => (
              <Link
                key={`mobile-nav-${link.href}`}
                to={link.href}
                className="flex items-center gap-2 px-4 py-3 border border-border font-heading tracking-wide text-sm"
                style={{ fontFamily: 'var(--font-heading)' }}
                onClick={() => setMobileOpen(false)}
              >
                <link.icon size={16} />
                {link.label.toUpperCase()}
              </Link>
            ))}

            <div className="border-t border-border pt-3">
              {isLoggedIn ? (
                <div className="flex flex-col gap-2">

                  <div className="flex items-center gap-2 px-3 py-2 border border-border">
                    <User
                      size={14}
                      className="text-muted-foreground"
                    />

                    <span className="text-sm font-heading tracking-wide">
                      {userName?.toUpperCase()} ·{' '}
                      {userRole?.toUpperCase()}
                    </span>
                  </div>

                  <button
                    onClick={() => {
                      onLogout?.();
                      setMobileOpen(false);
                    }}
                    className="btn-danger px-4 py-2 text-sm flex items-center gap-2"
                  >
                    <LogOut size={14} />
                    LOGOUT
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">

                  <Link
                    to="/login"
                    className="btn-secondary px-4 py-2 text-sm text-center"
                    onClick={() => setMobileOpen(false)}
                  >
                    LOG IN
                  </Link>

                  <Link
                    to="/login?tab=register"
                    className="btn-primary px-4 py-2 text-sm text-center"
                    onClick={() => setMobileOpen(false)}
                  >
                    REGISTER
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}